import { parseDate } from 'lib/date';
import Summary from 'models/aiArtifacts/Summary';
import AIJob, { AIJobOptions } from 'models/AIJob';
import AIPrompt, { AIPromptOptions } from 'models/AIPrompt';
import Attachment, { AttachmentOptions } from 'models/Attachment';
import User, { UserOptions } from 'models/User';

export type SummarizationPatient = {
  name: string;
  gender: string;
  dateOfBirth: string;
};

export type SummarizationData = {
  patient: SummarizationPatient;
  [key: string]: any;
};

export interface SummarizationOptions {
  id: string;
  title: string;
  data: SummarizationData;
  archived: boolean;
  createdAt: Date | string;
  updatedAt: Date | string;
  aiPromptId: string;
  createdBy?: Partial<UserOptions> | null;
  updatedBy?: Partial<UserOptions> | null;
  attachmentCount?: number;
  attachments?: Partial<AttachmentOptions>[];
  aiPrompt?: Partial<AIPromptOptions> | null;
  summaryJobForCurrentAttachments?: AIJobOptions | null;
}

function getDefaults(): SummarizationOptions {
  return {
    id: '',
    title: '',
    data: {
      patient: {
        name: '',
        gender: '',
        dateOfBirth: '',
      },
    },
    archived: false,
    createdAt: '',
    updatedAt: '',
    aiPromptId: '',
    createdBy: undefined,
    updatedBy: undefined,
    attachmentCount: 0,
    attachments: [],
  };
}

/**
 * @class Summarization
 * @classdesc Represents a standalone summarization in the system (not tied to patient context)
 * @property {string} id - The summarization's id
 * @property {string} title - The summarization's title
 * @property {SummarizationData} data - The summarization data containing patient info and other key-value pairs
 * @property {boolean} archived - Whether the summarization is archived
 * @property {Date | null} createdAt - The summarization's creation date
 * @property {Date | null} updatedAt - The summarization's last update date
 * @property {string} aiPromptId - The ID of the AI prompt used for summarization
 * @property {UserOptions} createdBy - The user who created the summarization
 * @property {UserOptions} updatedBy - The user who last updated the summarization
 * @property {number} attachmentCount - The number of attachments associated with the summarization
 * @property {Attachment[]} attachments - Files attached to the summarization
 * @property {AIPrompt | null} aiPrompt - The AI prompt used for summarization
 * @property {AIJob | null} summaryJobForCurrentAttachments - The AI job for generating the summary
 * @param {Partial<SummarizationOptions>} [options={}]
 * @example const summarization = new Summarization({ id: '123', title: 'Patient Summary' });
 */
export default class Summarization {
  id: string;
  title: string;
  data: any;
  archived: boolean;
  createdAt: Date | null;
  updatedAt: Date | null;
  aiPromptId: string;
  createdBy?: UserOptions;
  updatedBy?: UserOptions;
  attachmentCount?: number;
  attachments: Attachment[];
  aiPrompt?: AIPrompt | null;
  summaryJobForCurrentAttachments: AIJob | null;

  constructor(options: Partial<SummarizationOptions>) {
    const opts = { ...getDefaults(), ...options };

    this.id = opts.id;
    this.title = opts.title;
    this.data = opts.data;
    this.archived = opts.archived;
    this.createdAt = parseDate(opts.createdAt) ?? null;
    this.updatedAt = parseDate(opts.updatedAt) ?? null;
    this.aiPromptId = opts.aiPromptId;
    this.createdBy = opts.createdBy ? new User(opts.createdBy) : undefined;
    this.updatedBy = opts.updatedBy ? new User(opts.updatedBy) : undefined;
    this.aiPrompt = opts.aiPrompt ? new AIPrompt(opts.aiPrompt) : undefined;
    this.attachmentCount = opts.attachmentCount;
    this.attachments = opts.attachments?.map((a) => new Attachment(a)) ?? [];
    this.summaryJobForCurrentAttachments = opts.summaryJobForCurrentAttachments
      ? new AIJob(opts.summaryJobForCurrentAttachments)
      : null;
  }

  get patient(): Partial<SummarizationPatient> | null {
    return this.data.patient;
  }

  get summaryJob(): AIJob | null {
    return this.summaryJobForCurrentAttachments;
  }

  get summary(): Summary | undefined {
    return this.summaryJob?.aiArtifact as Summary | undefined;
  }
}
